<template>
  <div class="pt-4 p-4 flex flex-col items-center min-h-screen text-light-text dark:text-dark-text">
    <AppSidebar />
    <div class="w-full max-w-6xl bg-white dark:bg-dark-box-bg rounded-lg shadow-md p-6 mt-6">
      <button
        @click="goToPayoffs"
        class="mb-4 text-teal-700 dark:text-teal-400 hover:underline flex items-center"
      >
        ← Wróć do zakładki Rozliczeń
      </button>

      <div v-if="loading" class="text-center text-gray-500 dark:text-gray-400">Ładowanie danych firmy...</div>
      <div v-else>
        <div class="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-4">
          <label for="month-filter" class="text-sm font-medium text-gray-700 dark:text-gray-300">Filtruj według miesiąca:</label>
          <select
            id="month-filter"
            v-model="selectedMonthFilter"
            @change="filterInvoices"
            class="block pl-3 pr-10 py-2 text-sm bg-light-bg text-light-text border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md dark:bg-dark-box-bg dark:text-dark-text dark:border-dark-border w-full sm:w-48"
          >
            <option value="current">Bieżący miesiąc</option>
            <option v-for="month in months" :key="month.value" :value="month.value">{{ month.label }}</option>
            <option value="all">Wszystkie</option>
          </select>

          <label for="year-filter" class="text-sm font-medium text-gray-700 dark:text-gray-300">Rok:</label>
          <select
            id="year-filter"
            v-model="selectedYear"
            @change="filterInvoices"
            class="block pl-3 pr-10 py-2 text-sm bg-light-bg text-light-text border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md dark:bg-dark-box-bg dark:text-dark-text dark:border-dark-border w-full sm:w-32"
          >
            <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
          </select>
        </div>
      </div>

      <!-- Faktury -->
      <div class="mt-6">
        <h2 class="text-xl font-semibold mb-4 text-gray-800 dark:text-white">Faktury</h2>
        <div class="overflow-x-auto">
          <table class="w-full border-collapse border border-light-border dark:border-dark-border">
            <thead>
              <tr class="bg-light-table-header dark:bg-dark-table-header">
                <th class="border px-4 py-2 text-left">#</th>
                <th class="border px-4 py-2 text-left">Numer faktury</th>
                <th class="border px-4 py-2 text-left">Rok</th>
                <th class="border px-4 py-2 text-left">Miesiąc</th>
                <th class="border px-4 py-2 text-left">Akcja</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(invoice, index) in filteredInvoices"
                :key="invoice.id"
                class="border-b hover:bg-gray-100 dark:hover:bg-dark-box-hover"
              >
                <td class="px-4 py-2">{{ index + 1 }}</td>
                <td class="px-4 py-2">{{ invoice.invoiceNumber }}</td>
                <td class="px-4 py-2">{{ invoice.year }}</td>
                <td class="px-4 py-2">{{ months[invoice.month - 1]?.label || invoice.month }}</td>
                <td class="px-4 py-2">
                  <button
                    @click="downloadInvoice(invoice.id, invoice.invoiceNumber)"
                    class="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400"
                  >
                    Pobierz PDF
                  </button>
                  <button
                    v-if="invoice.invoiceNumber.startsWith('FV')"
                    @click="regenerate(invoice.id, invoice.invoiceNumber)"
                    class="ml-4 px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 dark:bg-yellow-500 dark:hover:bg-yellow-400"
                  >
                    Wygeneruj Ponownie
                  </button>
                </td>
              </tr>
              <tr v-if="filteredInvoices.length === 0">
                <td colspan="6" class="text-center py-4 text-gray-500 dark:text-gray-400">
                  Brak faktur dla wybranych filtrów.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { useAuthStore } from '@/stores/authStore';
import AppSidebar from '../AppSidebar.vue';
import invoiceService from '@/services/invoiceService';
import companiesService from '@/services/companiesService';

export default {
  name: 'InvoicesDispatcher',
  components: {
    AppSidebar
  },
  setup() {
    const authStore = useAuthStore();
    authStore.checkExpiration();
    return { user: authStore.user };
  },
  data() {
    return {
      company: {
        id: 0,
        companyName: '',
        companyNip: '',
        companyAddress: '',
        phone: 0,
        orders: [],
        onlyAB: false,
        extendedInvoice: false
      },
      invoices: [], // Wszystkie faktury pobrane z serwera
      filteredInvoices: [], // Faktury po filtracji
      selectedMonthFilter: 'current',
      selectedYear: new Date().getFullYear(),
      currentPage: 1,
      itemsPerPage: 8,
      totalPages: 0,
      visiblePages: [],
      message: '',
      loading: true,
      months: [
        { value: '1', label: 'Styczeń' },
        { value: '2', label: 'Luty' },
        { value: '3', label: 'Marzec' },
        { value: '4', label: 'Kwiecień' },
        { value: '5', label: 'Maj' },
        { value: '6', label: 'Czerwiec' },
        { value: '7', label: 'Lipiec' },
        { value: '8', label: 'Sierpień' },
        { value: '9', label: 'Wrzesień' },
        { value: '10', label: 'Październik' },
        { value: '11', label: 'Listopad' },
        { value: '12', label: 'Grudzień' },
      ],
      years: [new Date().getFullYear()],
    };
  },
  mounted() {
    this.fetchCompanyAndInvoices();
  },
  watch: {
    invoices: {
      immediate: true,
      handler() {
        this.filterInvoices();
      }
    },
    selectedMonthFilter() {
      this.filterInvoices();
    },
    selectedYear() {
      this.filterInvoices();
    },
    filteredInvoices() {
      this.calculateTotalPages();
    }
  },
  methods: {
    filterInvoices() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; // miesiące od 1 do 12

      if (!Array.isArray(this.invoices)) {
        this.filteredInvoices = [];
        return;
      }

      this.filteredInvoices = this.invoices.filter(invoice => {
        if (invoice.year == null || invoice.month == null) return false;

        if (this.selectedMonthFilter === 'current') {
          return invoice.year === currentYear && invoice.month === currentMonth;
        } else if (this.selectedMonthFilter === 'all') {
          return invoice.year === this.selectedYear;
        } else {
          const monthIndex = parseInt(this.selectedMonthFilter, 10);
          return invoice.year === this.selectedYear && invoice.month === monthIndex;
        }
      });
      // paginate i inne rzeczy możesz zrobić tutaj lub w watcherze filteredInvoices
    },
    async fetchCompanyAndInvoices() {
      try {
        const companyId = this.$route.params.id;
        const res = await companiesService.getCompanyById(companyId);
        this.company = res.data;
        this.loading = false;

        await this.fetchInvoices(companyId);
      } catch (error) {
        this.loading = false;
        console.error('Błąd pobierania danych firmy:', error);
        this.message = 'Nie udało się pobrać danych firmy.';
      }
    },
    async fetchInvoices(companyId) {
      try {
        const response = await invoiceService.getAllByCompanyId(companyId);
        if (!response.data || !Array.isArray(response.data)) {
          this.invoices = [];
        } else {
          this.invoices = response.data;
        }
        console.log('Pobrane faktury:', this.invoices);
      } catch (error) {
        console.error('Błąd pobierania faktur:', error);
        this.message = 'Nie udało się pobrać faktur.';
      }
    },
    calculateTotalPages() {
      this.totalPages = Math.ceil(this.filteredInvoices.length / this.itemsPerPage);
      this.updateVisiblePages();
    },
    updateVisiblePages() {
      const range = 5;
      const start = Math.max(1, this.currentPage - Math.floor(range / 2));
      const end = Math.min(start + range - 1, this.totalPages);
      this.visiblePages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
    goToPage(page) {
      this.currentPage = page;
      this.updateVisiblePages();
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.updateVisiblePages();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.updateVisiblePages();
      }
    },
    goToPayoffs() {
      const returnTo = this.$route.query.returnTo;
      const currentPage = this.$route.query.currentPage;
      if (returnTo) {
        this.$router.push({
          name: returnTo,
          query: { currentPage: currentPage }
        });
      } else {
        if (this.user.role === 'ROLE_ADMIN') {
          this.$router.push({ name: 'PayoffCompanyProfileAdmin', query: { currentPage: currentPage } });
        } else {
          this.$router.push({ name: 'PayoffCompanyProfileDispatcher', query: { currentPage: currentPage } });
        }
      }
    },
    async downloadInvoice(invoiceId, invoiceNumber) {
        try {
            const response = await invoiceService.downloadInvoice(invoiceId);
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${invoiceNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Błąd pobierania faktury:', error);
        }
    },
    async regenerate(invoiceId) {
        try {
            await invoiceService.regenerateInvoice(invoiceId);
            this.fetchCompanyAndInvoices();
        } catch (error) {
            console.error('Błąd pobierania faktury:', error);
        }
    }
  }
};
</script>

